/* eslint-disable sonarjs/no-duplicate-string */
import {
  animate,
  animation,
  AnimationReferenceMetadata,
  AnimationTriggerMetadata,
  keyframes,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { IAnimationOptions, useAnimationIncludingChildren } from '@commons/animations/util';

export const fadeIn = (): AnimationReferenceMetadata =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ visibility: 'visible', opacity: 0, easing: 'ease', offset: 0 }),
        style({ opacity: 0.7, easing: 'ease', offset: 1 }),
      ])
    ),
  ]);

export function fadeInOnEnterAnimation(): AnimationTriggerMetadata {
  return trigger('fadeInOnEnter', [
    transition(
      ':enter',
      [style({ visibility: 'hidden' }), ...useAnimationIncludingChildren(fadeIn(), { duration: 300 })],
      {
        params: {
          delay: 0,
          duration: 300,
        },
      }
    ),
  ]);
}

export const fadeOut = (): AnimationReferenceMetadata =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([style({ opacity: 0.7, easing: 'ease', offset: 0 }), style({ opacity: 0, easing: 'ease', offset: 1 })])
    ),
  ]);

export function fadeOutAnimation(): AnimationTriggerMetadata {
  return trigger('fadeOut', [
    transition('0 => 1', [...useAnimationIncludingChildren(fadeOut(), { duration: 300 })], {
      params: {
        delay: 0,
        duration: 300,
      },
    }),
  ]);
}

export function fadeOutOnLeaveAnimation(): AnimationTriggerMetadata {
  return trigger('fadeOutOnLeave', [
    transition(':leave', [...useAnimationIncludingChildren(fadeOut(), { duration: 300 })], {
      params: {
        delay: 0,
        duration: 300,
      },
    }),
  ]);
}

export interface IFadeOutLeftAnimationOptions extends IAnimationOptions {
  /**
   * Translate, possible units: px, %, em, rem, vw, vh
   *
   * Default: 100%
   */
  translate?: string;
}

const fadeOutLeft = (): AnimationReferenceMetadata =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({ opacity: 1, transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 0 }),
        style({ opacity: 0, transform: 'translate3d(-{{translate}}, 0, 0)', easing: 'ease', offset: 1 }),
      ])
    ),
  ]);

export function fadeOutLeftAnimation(): AnimationTriggerMetadata {
  return trigger('fadeOutLeft', [
    transition('0 => 1', [...useAnimationIncludingChildren(fadeOutLeft(), { duration: 300 })], {
      params: {
        delay: 0,
        duration: 300,
        translate: '100%',
      },
    }),
  ]);
}

export interface IFadeInLeftAnimationOptions extends IAnimationOptions {
  /**
   * Translate, possible units: px, %, em, rem, vw, vh
   *
   * Default: 100%
   */
  translate?: string;
}

const fadeInLeft = (): AnimationReferenceMetadata =>
  animation([
    animate(
      '{{duration}}ms {{delay}}ms',
      keyframes([
        style({
          visibility: 'visible',
          opacity: 0,
          transform: 'translate3d(-{{translate}}, 0, 0)',
          easing: 'ease',
          offset: 0,
        }),
        style({ opacity: 1, transform: 'translate3d(0, 0, 0)', easing: 'ease', offset: 1 }),
      ])
    ),
  ]);

export function fadeInLeftAnimation(): AnimationTriggerMetadata {
  return trigger('fadeInLeft', [
    transition(
      '0 => 1',
      [style({ visibility: 'hidden' }), ...useAnimationIncludingChildren(fadeInLeft(), { duration: 300 })],
      {
        params: {
          delay: 0,
          duration: 300,
          translate: '100%',
        },
      }
    ),
  ]);
}

export function fadeInLeftOnEnterAnimation(): AnimationTriggerMetadata {
  return trigger('fadeInLeftOnEnter', [
    transition(
      ':enter',
      [style({ visibility: 'hidden' }), ...useAnimationIncludingChildren(fadeInLeft(), { duration: 300 })],
      {
        params: {
          delay: 0,
          duration: 300,
          translate: '100%',
        },
      }
    ),
  ]);
}
